import { FunctionComponent, useState } from 'react';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemText } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { DocsNavItem } from '../../../models/docs';

interface DocsNavListProps {
  items: DocsNavItem[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}

// Recursive navigation tree. The active page is highlighted; branches with
// children are collapsible and auto-expand when they contain the active slug.
export const DocsNavList: FunctionComponent<DocsNavListProps> = ({ items, activeSlug, onSelect }) => (
  <List
    disablePadding
    component="nav">
    {items.map((item) => (
      <DocsNavNode
        key={item.slug}
        item={item}
        depth={0}
        activeSlug={activeSlug}
        onSelect={onSelect}
      />
    ))}
  </List>
);

interface DocsNavNodeProps {
  item: DocsNavItem;
  depth: number;
  activeSlug: string;
  onSelect: (slug: string) => void;
}

function containsSlug(item: DocsNavItem, slug: string): boolean {
  if (item.slug === slug) return true;
  return item.children.some((child) => containsSlug(child, slug));
}

const DocsNavNode: FunctionComponent<DocsNavNodeProps> = ({ item, depth, activeSlug, onSelect }) => {
  const hasChildren = item.children.length > 0;
  const [open, setOpen] = useState<boolean>(() => hasChildren && containsSlug(item, activeSlug));
  const selected = item.slug === activeSlug;
  const renderedOpen = open || (hasChildren && containsSlug(item, activeSlug));

  return (
    <Box>
      <ListItemButton
        selected={selected}
        onClick={() => onSelect(item.slug)}
        sx={{ pl: 2 + depth * 2, py: 0.75 }}>
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 600 : 400 }}
        />
        {hasChildren && (
          <IconButton
            size="small"
            edge="end"
            aria-label={renderedOpen ? 'Collapse section' : 'Expand section'}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
            }}>
            {renderedOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse
          in={renderedOpen}
          timeout="auto"
          unmountOnExit>
          <List
            disablePadding
            component="div">
            {item.children.map((child) => (
              <DocsNavNode
                key={child.slug}
                item={child}
                depth={depth + 1}
                activeSlug={activeSlug}
                onSelect={onSelect}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};
